import { useMemo, useState } from 'react';
import { PersonFace } from '../person-face';

import {
  lineItemHasWork,
  lineItemMoneyPeriods,
  lineItemTotal,
  lineItemSpan,
  occurrences,
  type Contract,
  type Metafield,
  type Occurrence,
  type LineItemEconomics,
  type OccurrenceEconomics,
  type ContractWorklogDay,
  type LineItem,
  type Recurrence,
} from '@forecastify/shared';
import { useDuration } from '../../duration';
import {
  Badge,
  Button,
  DropdownMenu,
  Icon,
  Stepper,
  Tooltip,
  type MenuItem,
} from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';
import { money } from './finance/finance-format';
import { Attachments } from '../attachments';
import { DOCUMENT_FILES } from '../file-kinds';
import { ScopedDropSurface } from '../scoped-drop';
import {
  ClosedNotice,
  DetailFrame,
  InlineTitle,
  MoreMenu,
  ProgressBlock,
  RailCard,
  RailDate,
  RailPicker,
  RailRow,
  RailValue,
  SectionAction,
  SectionHeader,
} from './detail-chrome';
import { CollapsibleHistory } from '../history/collapsible-history';
import { MetafieldsCard } from './metafields-card';
import { OccurrencesCard, type PeriodMenuHandlers } from './occurrences-card';
import { LINE_ITEM_TYPE_KEY } from './line-item-types';
import { contractLabel } from './contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C24 · The contract detail BODY. One component; three frames wrap it.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `2fazis.ContractDetail.html`, **re-transcribed**. The first pass built this screen out of the
 * product's generic parts — `DetailBox`, `<h3>`, an HTML `<table>`, a `Select` per rail row — and
 * every one of those was defensible on its own while the sum looked adjacent to the product rather
 * than part of it. `detail-chrome.tsx` holds the shapes the mockup actually uses, with the argument
 * for each; this file assembles them.
 *
 * The layout is the mockup's own three-slot grid by explicit coordinates: the header spans both
 * columns, the rail is column two, the main column is row two beside it — and in a drawer all three
 * stack with the **rail second**, because status and recurrence are what somebody opened it for.
 *
 * ⚠️⚠️ **THE MOCKUP DRAWS MONEY IN THIS TABLE AND NONE OF IT IS BUILT.** Its line-item row carries
 * *Büdzsé* and *Bevétel*, and its status card a *Készültség* bar; `decisions.md` P2-1 defers the
 * financial layer, and the prototype's own logic never fills those keys either — open it and the
 * cells are blank. `docs/mockup/PHASE-2.md` lists them. **Nor are `Kezdés`/`Vég` on that row filled**
 * — `li.fromLabel` is drawn and never set — and those two ARE built here, because we have the dates
 * and a column with a header and no values is worse than either choice.
 *
 * ⚠️ **`Időszakok` is `occurrences-card.tsx`**, and it renders nothing for a contract that does not
 * recur — so this file never has to know what a period is.
 */

export interface ContractDetailData {
  readonly contract: Contract;
  readonly lineItems: readonly LineItem[];
  /**
   * The contract's hours per day, unbucketed — the periods table files them by date. 31 Oct 2026:
   * days rather than every worklog, which stopped silently at 5,000.
   *
   * ⚠️ **Read separately and non-fatal**: a contract renders without its hours,
   * with a periods table that says zero, which is honest for somebody who cannot see worklogs.
   */
  readonly worklogs: readonly ContractWorklogDay[];
  /** The single collector task, when the contract has one — P2-10. */
  readonly collector: { readonly id: string; readonly key: string; readonly title: string } | null;
  /** C30 · the workspace's `contract` metafields. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  /** For the `user` metafield type. */
  readonly members: readonly { readonly id: string; readonly name: string }[];
  /**
   * C55 · every period of a recurring contract, priced, for the *Időszakok* table's money columns.
   * ⚠️ **Read separately and non-fatal**: empty is *not loaded or not allowed*, and the columns
   * hide rather than claim zero.
   */
  readonly periodMoney: readonly OccurrenceEconomics[];
  /**
   * 17 Oct 2026 · the line items, priced — `cost` for the *Költés* column. Empty is *not loaded or
   * not allowed*, and the column draws – rather than a zero.
   */
  readonly lineItemMoney: readonly LineItemEconomics[];
  /** The workspace's currencies, for the symbol beside each figure. */
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
}

export interface ContractDetailHandlers {
  /** `contract.manage`. UI gating is UX; the server checks again. */
  readonly canManage: boolean;
  /** `lineitem.manage` — deliberately a different answer. See `contract.service.ts`. */
  readonly canManageLineItems: boolean;
  /** ⚠️ Whether the line item's type is shown — `contracts.line_item_pricing` (P2-1). */
  readonly pricing: boolean;
  readonly onPatch: (patch: Record<string, unknown>) => void;
  readonly onPatchLineItem: (item: LineItem, patch: Record<string, unknown>) => void;
  readonly onDeleteLineItem: (item: LineItem) => void;
  /**
   * Opens the create dialog.
   *
   * ⚠️⚠️ **CREATE ONLY — it used to take a line item and open the same dialog on it.** Feedback
   * round 4 item 7: the detail PAGE is the editor, so the row's *Szerkesztés* now opens it rather
   * than a second editor for the same five fields. `line-item-dialog.tsx` argues why one of the two
   * had to go and why the page is the one that stays.
   */
  readonly onNewLineItem: () => void;
  readonly onOpenLineItem: (item: LineItem) => void;
  /** Uploads one file against this contract. Resolves to an error CODE, or null on success. */
  readonly onAttach: (file: File) => Promise<string | null>;
  readonly onOpenProject: () => void;
  /** C56 · ⚠️ Absent where the screen cannot do it — the drawer inside a project, say. */
  readonly onDetachProject?: () => void;
  readonly onOpenTask: (taskId: string) => void;
  /** C29 · opens one derived period. */
  readonly onOpenPeriod: (period: Occurrence) => void;
  /** 17 Oct 2026 · a period row's menu — the phase 2 mockup's `occMenu`. */
  readonly periodMenu?: PeriodMenuHandlers;
  readonly onMetafields: (values: Record<string, unknown>) => void;
  /** Grows the detail to the full-page frame. Absent when it is already there. */
  readonly onExpand?: () => void;
}

export function ContractDetail({
  data,
  handlers,
  wide,
}: {
  readonly data: ContractDetailData;
  readonly handlers: ContractDetailHandlers;
  readonly wide: boolean;
}) {
  const { contract } = data;
  /* ⚠️ Bumped after a drop so the attachment list re-reads — it owns its own state. */
  const [dropped, setDropped] = useState(0);

  return (
    /**
     * ⚠️⚠️ **THE WHOLE RECORD TAKES A FILE — 14 Sep, the Jira gesture.** A contract has no
     * description field and no comment thread, so there is exactly one destination and the veil
     * says so plainly. The surface still earns its place: a signed PDF arrives weeks after the
     * contract was drafted, and having to find the attachments section and then a file picker is
     * three steps where dragging is none.
     */
    <ScopedDropSurface
      entityLabel={contractLabel(contract)}
      enabled={handlers.canManage}
      zoneLabels={{}}
      className="contents"
      onFiles={(files) => {
        void (async () => {
          /* ⚠️ Sequential: each file is scanned, and firing five at one clamd times a scan out. */
          for (const file of files) {
            await handlers.onAttach(file);
          }
          setDropped((count) => count + 1);
        })();
      }}
    >
      <DetailFrame
        wide={wide}
        header={<Header data={data} handlers={handlers} />}
        rail={
          <>
            <StatusCard data={data} handlers={handlers} />
            {contract.recurrence === null ? null : (
              <RecurrenceCard recurrence={contract.recurrence} handlers={handlers} />
            )}
            <DetailsCard data={data} handlers={handlers} />
            {/* 22 Oct 2026 · a project reader without its money sees the contract, not its sums. */}
            {contract.moneyVisible ? <MoneySummaryCard data={data} /> : null}
          </>
        }
        main={
          <>
            <LineItems data={data} handlers={handlers} wide={wide} />
            {/* ⚠️ Renders nothing for a contract that does not recur — the card decides, so the
              detail does not have to know what a period is. */}
            <OccurrencesCard
              contract={contract}
              lineItems={data.lineItems}
              worklogs={data.worklogs}
              collector={data.collector}
              economics={data.periodMoney}
              currencies={data.currencies}
              onOpenTask={handlers.onOpenTask}
              onOpenPeriod={handlers.onOpenPeriod}
              {...(handlers.periodMenu === undefined ? {} : { menu: handlers.periodMenu })}
            />
            <MetafieldsCard
              fields={data.metafields}
              values={contract.metafields}
              members={data.members}
              canEdit={handlers.canManage}
              onSave={handlers.onMetafields}
            />
            <ContractAttachments contract={contract} handlers={handlers} dropped={dropped} />
            <History data={data} />
          </>
        }
      />
    </ScopedDropSurface>
  );
}

/* ── header ──────────────────────────────────────────────────────────────────────────────── */

function Header({
  data,
  handlers,
}: {
  readonly data: ContractDetailData;
  readonly handlers: ContractDetailHandlers;
}) {
  const t = useT();
  const { contract } = data;

  const menu: MenuItem[] = [
    ...(handlers.canManageLineItems
      ? [
          {
            label: t('contract.lineItem.new'),
            icon: 'plus' as const,
            onSelect: () => {
              handlers.onNewLineItem();
            },
          },
        ]
      : []),
    ...(handlers.onExpand === undefined
      ? []
      : [{ label: t('task.mode.full'), icon: 'maximize' as const, onSelect: handlers.onExpand }]),
    /**
     * C56 · ⚠️⚠️ **LEVÁLASZTÁS, AND IT IS OFFERED RATHER THAN HIDDEN WHEN IT WOULD BE
     * REFUSED.** The server counts the tasks that tie this contract to its project and answers
     * with both figures; hiding the item would leave somebody wondering whether the feature
     * exists, which is the argument `people.test.tsx` records for the disabled delete.
     *
     * ⚠️ Only for a contract that HAS a project — there is nothing to detach otherwise.
     */
    ...(handlers.canManage && contract.projectId !== null && handlers.onDetachProject !== undefined
      ? [
          { type: 'separator' as const },
          {
            label: t('contract.action.detach'),
            /* ⚠️ `link`, not `unlink`: the design system's 77 have no `unlink`, and adding a
               glyph for one menu item is an addition to flag rather than a convenience. This
               one names the thing being acted on — the project link — and the label says
               what happens to it. */
            icon: 'link' as const,
            onSelect: () => {
              handlers.onDetachProject?.();
            },
          },
        ]
      : []),
    ...(handlers.canManage
      ? [
          { type: 'separator' as const },
          contract.status === 'closed'
            ? {
                label: t('contract.action.reopen'),
                icon: 'circle-plus' as const,
                onSelect: () => {
                  handlers.onPatch({ status: 'active' });
                },
              }
            : {
                /* ⚠️ `destructive`, not `danger` — `dropdown-menu.tsx`'s own note. */
                label: t('contract.action.close'),
                icon: 'check' as const,
                destructive: true,
                onSelect: () => {
                  handlers.onPatch({ status: 'closed' });
                },
              },
        ]
      : []),
  ];

  return (
    <>
      {/**
       * ┌───────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ ONE META LINE, THEN THE TITLE BESIDE THE KEBAB — item 33.    │
       * └───────────────────────────────────────────────────────────────────┘
       *
       * It was two rows of widgets: a code chip, a project button and a type BADGE on the first,
       * the title alone on the second, and the kebab stranded at the end of the first — so the
       * one thing a reader is looking for sat below three chips, and on a narrow drawer the
       * chips wrapped and made it three rows.
       *
       * ⚠️ **`HSO-C01 · Haystack (milestone-based)` as ONE LINE OF TEXT**, with the separators
       * drawn rather than implied by spacing. The code and the project stay clickable — they are
       * a copy-link and a navigation — but they read as a sentence rather than as three controls.
       *
       * ⚠️ **The type loses its `Badge`.** A badge is for a state that changes and matters at a
       * glance; a contract's type is fixed at creation (the rail says so) and was drawing the eye
       * to the least surprising fact on the screen.
       */}
      <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-[12px]">
        {/* ⚠️⚠️ 3 Nov 2026 · **PLAIN TEXT NOW — THE COPY LIVES BESIDE THE BREADCRUMB.** This was a
            second copy-link button, and its link was built without the workspace prefix
            (`origin/contracts/…`), so what it copied opened nothing. The trail's
            `RecordCopyMenu` offers the identifier and the canonical link, on every frame. */}
        <span className="shrink-0 px-1.5 text-[12px] tabular-nums">{contractLabel(contract)}</span>
        <span aria-hidden>·</span>
        <button
          type="button"
          className="hover:text-foreground flex min-w-0 items-center gap-1.5 transition-colors"
          onClick={handlers.onOpenProject}
        >
          <span
            className="size-2 shrink-0 rounded-[3px]"
            style={{ background: contract.projectColor ?? 'transparent' }}
            aria-hidden
          />
          <span className="truncate">{contract.projectName}</span>
        </button>
        <span className="shrink-0">({t(TYPE_KEY[contract.type])})</span>
      </div>

      {/* ⚠️ **The kebab moved HERE, beside the title** — item 33. It is the menu FOR this
          contract, and hanging it off the meta line put it beside the project's name instead. */}
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <InlineTitle
            value={contract.name}
            canEdit={handlers.canManage}
            onSave={(name) => {
              handlers.onPatch({ name });
            }}
          />
        </div>
        {menu.length === 0 ? null : <MoreMenu items={menu} label={t('common.more')} />}
      </div>

      {/* ⚠️ **The consequence, not the state.** The status card already says *lezárt*; this says
          what that MEANS — no new task and no worklog on its line items — which is the only reason
          worth saying it twice. */}
      {contract.status === 'closed' ? (
        <ClosedNotice>{t('contract.closedNotice')}</ClosedNotice>
      ) : null}
    </>
  );
}

/* ── the rail ────────────────────────────────────────────────────────────────────────────── */

const STATUS_COLOR = {
  draft: 'var(--muted-foreground)',
  active: 'var(--primary)',
  closed: 'var(--muted-foreground)',
} as const;

function StatusCard({
  data,
  handlers,
}: {
  readonly data: ContractDetailData;
  readonly handlers: ContractDetailHandlers;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const { contract } = data;

  /**
   * ⚠️⚠️ **THE SAME MULTIPLICATION THE LINE-ITEM TABLE DOES, AND FOR THE SAME REASON.** The server
   * sums the line items' estimates, which under a recurring contract are per period — so the raw
   * sum is one month's commitment, not the year's. Showing 64:00 here beside 480:00 in the table
   * would be two numbers about the same thing that disagree, and the reader would trust the wrong
   * one. `contract.estimateHours` stays per period on the wire; both screens multiply.
   *
   * ⚠️ **Each line item by ITS OWN span (`lineItemSpan`), not the contract's period count** — a
   * deliverable that starts in month four spans fewer periods, and the table's rows multiply the
   * same way, so the card is their sum rather than a figure beside it.
   */
  const estimate = useMemo(
    () =>
      data.lineItems.reduce(
        (total, item) =>
          total +
          Number(item.estimateHours) *
            Math.max(1, lineItemSpan(contract.recurrence, contract.endsOn, item)),
        0,
      ),
    [data.lineItems, contract.recurrence, contract.endsOn],
  );
  const tracked = Number(contract.trackedHours);
  const pct = estimate > 0 ? Math.round((tracked / estimate) * 100) : 0;

  return (
    <RailCard title={t('contract.column.status')} padded>
      <DropdownMenu
        label={t('contract.column.status')}
        width={240}
        align="start"
        className="mt-1.5 w-full"
        items={CONTRACT_STATUS_OPTIONS.map((status) => ({
          label: t(STATUS_KEY[status]),
          dot: STATUS_COLOR[status],
          checked: contract.status === status,
          disabled: !handlers.canManage,
          onSelect: () => {
            handlers.onPatch({ status });
          },
        }))}
      >
        <button
          type="button"
          className="border-input hover:bg-accent/60 flex h-8 w-full items-center gap-2 rounded-md border px-2.5 text-left text-[12.5px] font-medium shadow-xs transition-colors"
        >
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: STATUS_COLOR[contract.status] }}
            aria-hidden
          />
          <span className="min-w-0 flex-1 truncate">{t(STATUS_KEY[contract.status])}</span>
          <Icon name="chevron-down" size={13} className="text-muted-foreground opacity-70" />
        </button>
      </DropdownMenu>

      {/* ⚠️ A percentage over a zero estimate is 0 rather than a division by nothing, and the
          sentence beside the bar carries both real figures either way. */}
      <ProgressBlock
        estimate={fmt(String(estimate))}
        tracked={fmt(contract.trackedHours)}
        pct={pct}
        over={estimate > 0 && tracked > estimate}
        label={t('contract.column.tracked')}
      />
    </RailCard>
  );
}

const RECURRENCE_UNITS = ['month', 'week'] as const;
const RECURRENCE_MODES = ['date', 'count'] as const;

function RecurrenceCard({
  recurrence,
  handlers,
}: {
  readonly recurrence: Recurrence;
  readonly handlers: ContractDetailHandlers;
}) {
  const t = useT();

  const patch = (part: Partial<Recurrence>): void => {
    handlers.onPatch({ recurrence: { ...recurrence, ...part } });
  };

  return (
    <RailCard title={t('contract.recurrence')} note={recurrenceSummary(recurrence, t)}>
      <RailRow label={t('contract.recurrence.unit')}>
        <RailPicker
          label={t('contract.recurrence.unit')}
          disabled={!handlers.canManage}
          value={t(
            recurrence.unit === 'week' ? 'contract.recurrence.week' : 'contract.recurrence.month',
          )}
          items={RECURRENCE_UNITS.map((unit) => ({
            label: t(unit === 'week' ? 'contract.recurrence.week' : 'contract.recurrence.month'),
            checked: recurrence.unit === unit,
            onSelect: () => {
              patch({ unit });
            },
          }))}
        />
      </RailRow>

      <RailRow label={t('contract.recurrence.step')}>
        {handlers.canManage ? (
          <Stepper
            min={1}
            max={12}
            aria-label={t('contract.recurrence.step')}
            decrementLabel={t('common.decrease')}
            incrementLabel={t('common.increase')}
            value={String(recurrence.step)}
            onChange={(next) => {
              /* ⚠️ Clamped here rather than sent and refused: the reader is mid-keystroke, and a
                 validation error about a field they are still typing is noise. */
              patch({ step: Math.max(1, Math.min(12, Number(next) || 1)) });
            }}
            className="h-[30px] border-transparent bg-transparent shadow-none"
          />
        ) : (
          <RailValue>{recurrence.step}</RailValue>
        )}
      </RailRow>

      <RailRow label={t('contract.recurrence.startsOn')}>
        <RailDate
          label={t('contract.recurrence.startsOn')}
          disabled={!handlers.canManage}
          value={recurrence.startsOn}
          onChange={(startsOn) => {
            if (startsOn !== '') patch({ startsOn });
          }}
        />
      </RailRow>

      <RailRow label={t('contract.recurrence.endMode')}>
        <RailPicker
          label={t('contract.recurrence.endMode')}
          disabled={!handlers.canManage}
          value={t(
            recurrence.endMode === 'count'
              ? 'contract.recurrence.byCount'
              : 'contract.recurrence.byDate',
          )}
          items={RECURRENCE_MODES.map((endMode) => ({
            label: t(
              endMode === 'count' ? 'contract.recurrence.byCount' : 'contract.recurrence.byDate',
            ),
            checked: recurrence.endMode === endMode,
            onSelect: () => {
              /* ⚠️ **Switching mode fills the other half**, because `contracts_recurrence_shape`
                 refuses a `count` recurrence with no count — a patch that only changed the mode
                 would be rejected by the database and the reader would get a 400 for a dropdown. */
              patch(
                endMode === 'count'
                  ? { endMode, count: recurrence.count ?? 12, endsOn: null }
                  : { endMode, endsOn: recurrence.endsOn },
              );
            },
          }))}
        />
      </RailRow>

      {recurrence.endMode === 'date' ? (
        <RailRow label={t('contract.recurrence.lastDay')}>
          <RailDate
            label={t('contract.recurrence.lastDay')}
            disabled={!handlers.canManage}
            value={recurrence.endsOn ?? ''}
            onChange={(endsOn) => {
              patch({ endsOn: endsOn === '' ? null : endsOn });
            }}
          />
        </RailRow>
      ) : (
        <RailRow label={t('contract.recurrence.count')}>
          {handlers.canManage ? (
            <Stepper
              min={1}
              max={200}
              aria-label={t('contract.recurrence.count')}
              decrementLabel={t('common.decrease')}
              incrementLabel={t('common.increase')}
              value={String(recurrence.count ?? 12)}
              onChange={(next) => {
                patch({ count: Math.max(1, Math.min(200, Number(next) || 1)) });
              }}
              className="h-[30px] border-transparent bg-transparent shadow-none"
            />
          ) : (
            <RailValue>{recurrence.count ?? '–'}</RailValue>
          )}
        </RailRow>
      )}
    </RailCard>
  );
}

/** `Havonta · 2026-12-31-ig` — the summary the mockup puts in the card's header strip. */
function recurrenceSummary(recurrence: Recurrence, t: Translate): string {
  const every =
    recurrence.step === 1
      ? t(recurrence.unit === 'week' ? 'contract.every.week' : 'contract.every.month')
      : t(recurrence.unit === 'week' ? 'contract.every.weeks' : 'contract.every.months', {
          step: recurrence.step,
        });
  if (recurrence.endMode === 'count' && recurrence.count !== null) {
    return `${every} · ${t('contract.recurrence.times', { count: recurrence.count })}`;
  }
  if (recurrence.endsOn === null) return `${every} · ${t('contract.openEnded')}`;
  return `${every} · ${t('contract.recurrence.until', { date: recurrence.endsOn })}`;
}

/**
 * The budget a line item's row draws and the rail adds up — `budgetOf`'s rule, on the stored row.
 *
 * ⚠️ R19 (3 Nov 2026) · **a licence (U-7) and a time-and-materials item have NONE**, whatever an
 * old row still holds: an item switched from fixed to T&M kept its `budget` column, the row drew
 * it and the rail added it, while the economics endpoint beside them answered `null`.
 */
function budgetFigure(item: LineItem): string | null {
  return item.type === 'license' || item.type === 'time_and_material' ? null : item.budget;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ PÉNZÜGY — the contract's line items, added up (14 Oct 2026, ux-pass-2).   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Read-only: every figure is edited on its line item, and this card only sums them.
 *
 * ⚠️ **Not gated beyond reading the contract**, because the line items table on this very page
 * already shows each budget and income to the same reader — hiding their sum would hide nothing.
 *
 * ⚠️ **Money is summed PER CURRENCY.** Two line items in two currencies have no total without a
 * rate, and a rate is a reading of a day this card does not know; one line per currency is honest.
 *
 * ⚠️ **The estimate is the whole commitment** — each line item's per-period figure × its own span,
 * the same sum the status card and the table make, so the three agree. ⚠️ **U-5 · and so are the
 * budget and the income** — stored per period on a recurring contract, totalled by
 * `lineItemMoneyPeriods` exactly as the table's cells are.
 */
function MoneySummaryCard({ data }: { readonly data: ContractDetailData }) {
  const { fmt } = useDuration();
  const t = useT();
  const { contract, lineItems } = data;

  const sumBy = (pick: (item: LineItem) => string | null): string => {
    const totals = new Map<string, number>();
    for (const item of lineItems) {
      const value = pick(item);
      if (value === null) continue;
      const code = item.currencyCode ?? '';
      const total = lineItemTotal(
        Number(value),
        lineItemMoneyPeriods(contract.recurrence, contract.endsOn, item),
      );
      totals.set(code, (totals.get(code) ?? 0) + total);
    }
    if (totals.size === 0) return '–';
    return [...totals.entries()]
      .map(([code, amount]) => {
        const symbol = data.currencies.find((one) => one.code === code)?.symbol ?? code;
        return money(amount, { code, symbol });
      })
      .join(' · ');
  };

  /* Each line item by its OWN span — the status card's figure, so the two agree. */
  const estimate = lineItems.reduce(
    (sum, item) =>
      sum +
      Number(item.estimateHours) *
        Math.max(1, lineItemSpan(contract.recurrence, contract.endsOn, item)),
    0,
  );

  return (
    <RailCard title={t('contract.finance')}>
      <RailRow label={t('lineItem.money.budget')}>
        <RailValue>{sumBy(budgetFigure)}</RailValue>
      </RailRow>
      <RailRow label={t('lineItem.money.revenue')}>
        <RailValue>{sumBy((item) => item.revenue)}</RailValue>
      </RailRow>
      <RailRow label={t('contract.column.estimate')}>
        <RailValue>{fmt(String(estimate))}</RailValue>
      </RailRow>
      <RailRow label={t('contract.column.tracked')}>
        <RailValue>{fmt(contract.trackedHours)}</RailValue>
      </RailRow>
    </RailCard>
  );
}

function DetailsCard({
  data,
  handlers,
}: {
  readonly data: ContractDetailData;
  readonly handlers: ContractDetailHandlers;
}) {
  const t = useT();
  const { contract } = data;

  return (
    <RailCard title={t('contract.details')}>
      {/* ⚠️ **Read-only after creation, where the mockup has a picker.** A contract's type decides
          whether it HAS a recurrence, and `contracts_recurrence_shape` refuses the two halves apart
          — so `service → recurring` needs a recurrence written in the same request, which a
          one-value dropdown cannot supply. The dialog asks at creation. */}
      <RailRow label={t('contract.column.type')}>
        <RailValue mono={false}>{t(TYPE_KEY[contract.type])}</RailValue>
      </RailRow>

      {/* ⚠️ **A link, where the mockup has a picker that MOVES the contract.** Moving one between
          projects would strand its line items' tasks on the old project (P2-8) and leave the code
          prefix naming a project it no longer belongs to, so `updateContractRequestSchema` has no
          field for it. This goes to the project instead. */}
      <RailRow label={t('contract.column.project')}>
        <Tooltip asChild content={t('contract.action.openProject')}>
          <button
            type="button"
            className="hover:bg-accent/60 flex h-[30px] w-full items-center gap-2 rounded-md px-2 text-left text-[12.5px] transition-colors"
            onClick={handlers.onOpenProject}
          >
            <span
              className="size-2 shrink-0 rounded-[3px]"
              style={{ background: contract.projectColor ?? 'transparent' }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate">{contract.projectName}</span>
            <Icon name="arrow-right" size={12} className="text-muted-foreground opacity-60" />
          </button>
        </Tooltip>
      </RailRow>

      {/**
       * ⚠️⚠️ **EDITABLE SINCE ITEM 37, AND THE SERVER ALWAYS ALLOWED IT.**
       * `updateContractRequestSchema` has carried `ownerMemberId` since C30; only this row was
       * read-only, so a contract whose owner left the company could not be handed to anybody
       * without a database write.
       *
       * ⚠️ **Searchable**, because a workspace's member list is the long kind — `Select`'s own
       * note draws the line at ten, and this picker is over the same population as the PM one.
       */}
      <RailRow label={t('contract.column.owner')}>
        <RailPicker
          label={t('contract.column.owner')}
          value={contract.ownerName}
          before={
            <PersonFace
              name={contract.ownerName}
              seed={contract.ownerAvatarSeed}
              url={contract.ownerAvatarUrl}
              colorKey={contract.ownerMemberId}
            />
          }
          disabled={!handlers.canManage}
          searchPlaceholder={t('contract.owner.search')}
          items={data.members.map((member) => ({
            label: member.name,
            onSelect: () => {
              handlers.onPatch({ ownerMemberId: member.id });
            },
          }))}
        />
      </RailRow>

      <RailRow label={t('contract.column.startsOn')}>
        <RailDate
          label={t('contract.column.startsOn')}
          disabled={!handlers.canManage}
          value={contract.startsOn}
          onChange={(startsOn) => {
            if (startsOn !== '') handlers.onPatch({ startsOn });
          }}
        />
      </RailRow>

      <RailRow label={t('contract.column.endsOn')}>
        <RailDate
          label={t('contract.column.endsOn')}
          disabled={!handlers.canManage}
          value={contract.endsOn ?? ''}
          onChange={(endsOn) => {
            handlers.onPatch({ endsOn: endsOn === '' ? null : endsOn });
          }}
        />
      </RailRow>

      <RailRow label={t('contract.lineItems')} divided>
        <RailValue>{data.lineItems.length}</RailValue>
      </RailRow>
      <RailRow label={t('contract.tasks')}>
        <RailValue>{data.lineItems.reduce((sum, item) => sum + item.taskCount, 0)}</RailValue>
      </RailRow>
      <RailRow label={t('contract.createdAt')}>
        <RailValue>{contract.createdAt.slice(0, 10)}</RailValue>
      </RailRow>
    </RailCard>
  );
}

/* ── line items ──────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️⚠️ **A BORDERED LIST OF FLEX ROWS, NOT A `<table>`** — and the difference is not cosmetic. The
 * mockup's row drops columns by width and pins the row menu to the right edge with `sticky right-0`
 * while the rest scrolls under it. A `<table>` does neither without fighting it, which is why the
 * first version had no row menu and no narrow behaviour at all.
 */
function LineItems({
  data,
  handlers,
  wide,
}: {
  readonly data: ContractDetailData;
  readonly handlers: ContractDetailHandlers;
  readonly wide: boolean;
}) {
  const t = useT();

  /**
   * ⚠️⚠️ **UNDER A RECURRING CONTRACT THIS COLUMN SHOWS THE WHOLE COMMITMENT, NOT ONE PERIOD.**
   * `0050` stores the per-period figure — which is what the dialog asks for and what the periods
   * table compares against — and 40 hours a month for a year is 480, which is the number somebody
   * checking a contract against its budget wants to see. The mockup shows 480 here too; it gets
   * there by storing the total and dividing in the dialog, which we do the other way round so that
   * changing the recurrence cannot leave a stale total behind.
   */
  const periodCount = useMemo(
    () => occurrences(data.contract.recurrence, data.contract.endsOn).length,
    [data.contract.recurrence, data.contract.endsOn],
  );

  return (
    <section>
      <SectionHeader
        title={t('contract.lineItems')}
        note={
          data.lineItems.length === 0 ? '' : t('common.count', { count: data.lineItems.length })
        }
      >
        {handlers.canManageLineItems ? (
          <SectionAction
            label={t('contract.lineItem.new')}
            onClick={() => {
              handlers.onNewLineItem();
            }}
          />
        ) : null}
      </SectionHeader>

      <div className="mt-1.5 overflow-x-auto rounded-lg border">
        {/* ⚠️ `w-max min-w-full`: every row is as wide as the widest, so a row's hover tint and its
            border run under the columns scrolled into view instead of stopping at the card's edge. */}
        <div className="flex w-max min-w-full flex-col">
          <div className="text-muted-foreground bg-muted/40 flex items-center gap-3 border-b px-3 py-1.5 text-[10.5px] font-medium tracking-wide uppercase">
            <span className="min-w-[160px] flex-1">{t('contract.lineItem')}</span>
            {handlers.pricing ? (
              <span className="w-[116px] shrink-0">{t('contract.column.type')}</span>
            ) : null}
            {wide ? (
              <span className="w-[80px] shrink-0">{t('contract.column.startsOn')}</span>
            ) : null}
            {wide ? <span className="w-[80px] shrink-0">{t('contract.column.endsOn')}</span> : null}
            <span className="w-[74px] shrink-0 text-right">{t('contract.column.estimate')}</span>
            <span className="w-[74px] shrink-0 text-right">{t('contract.column.tracked')}</span>
            {/**
             * ┌────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ BÜDZSÉ, BEVÉTEL, FELADAT — round 5 item 16.                         │
             * └────────────────────────────────────────────────────────────────┘
             *
             * ⚠️⚠️ **AND *FELADAT* STOPS BEING `wide`-ONLY.** It was there already and hidden in the
             * drawer, which is why the report lists three missing columns rather than two: from the
             * reader's side a column that is not drawn is a column that does not exist.
             *
             * ⚠️ **The money is NOT `wide`-gated either.** A table that drops the two figures a
             * contract is argued over, the moment it is opened in a drawer, is the drawer telling
             * somebody to open the page instead. The row scrolls sideways — see the kebab.
             */}
            {data.contract.moneyVisible && (
              <>
                <span className="w-[96px] shrink-0 text-right">{t('lineItem.money.budget')}</span>
                <span className="w-[96px] shrink-0 text-right">{t('lineItem.money.revenue')}</span>
                <span className="w-[96px] shrink-0 text-right">{t('money.spend')}</span>
              </>
            )}
            <span className="w-[68px] shrink-0 text-right">{t('contract.tasks')}</span>
            {/* ⚠️⚠️ **STICKY, LIKE THE CELL UNDER IT.** The kebab column pins to the right so it
              survives the horizontal scroll the new columns cause — round 5 item 16 asks for
              exactly that. A header spacer that did not pin would slide out from over its own
              column and leave the menu under the wrong heading. */}
            {/* ⚠️ Opaque: a translucent tint let the scrolled headings show through it. */}
            <span className="sticky right-0 w-7 shrink-0 self-stretch bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))]" />
          </div>

          {data.lineItems.map((item) => (
            <LineItemRow
              key={item.id}
              item={item}
              handlers={handlers}
              wide={wide}
              /* ⚠️ The item's OWN span — one that starts later spans fewer periods. */
              periodCount={lineItemSpan(data.contract.recurrence, data.contract.endsOn, item)}
              spent={data.lineItemMoney.find((one) => one.lineItemId === item.id)}
            />
          ))}

          {data.lineItems.length === 0 ? (
            <div className="text-muted-foreground px-3 py-6 text-center text-[11.5px]">
              {t('contract.lineItems.empty')}
            </div>
          ) : null}
        </div>
      </div>

      {/* ⚠️ **The column is the WHOLE commitment and the field is per period**, which is exactly
          the sentence somebody needs when 40 becomes 480. Said once, here, rather than in a column
          nobody asked for. */}
      {periodCount > 1 && data.lineItems.length > 0 ? (
        <p className="text-muted-foreground mt-1.5 text-[11px]">
          {t('contract.perPeriodNote', { count: periodCount })}
        </p>
      ) : null}
    </section>
  );
}

/**
 * ⚠️ **The currency CODE stands in for the symbol — round 5 item 16.** `money()` wants a
 * `{ code, symbol }`, and this row has neither a symbol nor the workspace's currency list: the
 * table lives in a detail that never reads one. `1 200 000 HUF` is longer than `1 200 000 Ft` and
 * it is unambiguous, which is the property that matters in a column of figures.
 */
function currencyOf(item: LineItem): { code: string; symbol: string } {
  return { code: item.currencyCode ?? '', symbol: item.currencyCode ?? '' };
}

/**
 * A budget or income cell: the stored figure × the periods the line item spans, with the
 * per-period figure in the title when there is more than one (U-5).
 */
function MoneyCell({
  value,
  item,
  periods,
}: {
  readonly value: string | null;
  readonly item: LineItem;
  readonly periods: number;
}) {
  const t = useT();
  return (
    <Tooltip
      asChild
      content={
        value === null || periods <= 1
          ? undefined
          : t('contract.estimateTotal', {
              perPeriod: money(Number(value), currencyOf(item)),
              count: periods,
            })
      }
    >
      <span className="text-muted-foreground w-[96px] shrink-0 text-right text-[11.5px] tabular-nums">
        {value === null ? '–' : money(lineItemTotal(Number(value), periods), currencyOf(item))}
      </span>
    </Tooltip>
  );
}

function LineItemRow({
  item,
  handlers,
  wide,
  periodCount,
  spent,
}: {
  readonly item: LineItem;
  readonly handlers: ContractDetailHandlers;
  readonly wide: boolean;
  /** 0 for a contract that does not recur — the estimate is then already the whole commitment. */
  readonly periodCount: number;
  /** 17 Oct 2026 · its economics, for *Költés*; absent while loading or when not allowed. */
  readonly spent: LineItemEconomics | undefined;
}) {
  const { fmt } = useDuration();
  const t = useT();
  const estimate = Number(item.estimateHours) * (periodCount === 0 ? 1 : periodCount);
  /* U-5 · the same span for the money, floored at one — `lineItemMoneyPeriods`' rule. */
  const periods = Math.max(1, periodCount);
  const over = estimate > 0 && Number(item.trackedHours) > estimate;
  /**
   * ⚠️⚠️ **C55 · A LICENCE DRAWS AN EM-DASH IN BOTH HOUR COLUMNS, NEVER `0:00`.** It has no
   * estimate by construction and nothing can be logged against it, so a zero would read as
   * *"nobody has started"* — the same distinction `tracks_time` makes on the people list, and the
   * same answer: the em-dash says *this is not that kind of row*.
   */
  const hasWork = lineItemHasWork(item.type);

  const menu: MenuItem[] = [
    {
      label: t('lineItem.action.open'),
      icon: 'arrow-right',
      onSelect: () => {
        handlers.onOpenLineItem(item);
      },
    },
    ...(handlers.canManageLineItems
      ? [
          item.status === 'closed'
            ? {
                label: t('lineItem.action.reopen'),
                icon: 'circle-plus' as const,
                onSelect: () => {
                  handlers.onPatchLineItem(item, { status: 'open' });
                },
              }
            : {
                label: t('lineItem.action.close'),
                icon: 'check' as const,
                onSelect: () => {
                  handlers.onPatchLineItem(item, { status: 'closed' });
                },
              },
          { type: 'separator' as const },
          {
            /* ⚠️ **Disabled with the REASON in the label** — the mockup's own wording,
               *"Törlés — 3 feladat van rajta"*. A greyed-out Delete with no explanation reads as a
               permission problem; the server refuses it with `line_item_in_use` either way. */
            label:
              item.taskCount > 0
                ? t('lineItem.action.deleteBlocked', { count: item.taskCount })
                : t('lineItem.action.delete'),
            icon: 'trash' as const,
            destructive: true,
            disabled: item.taskCount > 0,
            onSelect: () => {
              handlers.onDeleteLineItem(item);
            },
          },
        ]
      : []),
  ];

  return (
    /* ⚠️⚠️ **A HOVER, BECAUSE THE ROW IS CLICKABLE AND NOTHING SAID SO — round 5 item 17.**
       *"Nincs hover animáció a soron, nem lehet látni hogy kattintható."* The same
       `hover:bg-accent/40` every other row in the product carries; `transition-colors` so it
       arrives rather than snapping, which is what makes it read as an affordance. */
    <div className="group hover:bg-accent/40 flex items-center gap-3 border-b px-3 py-2 transition-colors last:border-0">
      <span className="flex min-w-[160px] flex-1 items-center gap-2">
        <span
          className="size-2 shrink-0 rounded-full"
          style={{
            background: item.status === 'closed' ? 'var(--muted-foreground)' : 'var(--primary)',
          }}
          aria-hidden
        />
        <button
          type="button"
          className={`min-w-0 truncate text-left text-[12.5px] font-medium ${
            item.status === 'closed' ? 'text-muted-foreground' : ''
          }`}
          onClick={() => {
            handlers.onOpenLineItem(item);
          }}
        >
          {item.name}
        </button>
        {item.status === 'closed' ? (
          <Badge variant="outline">{t('contract.lineItem.closed')}</Badge>
        ) : null}
      </span>

      {handlers.pricing ? (
        <span className="text-muted-foreground w-[116px] shrink-0 truncate text-[11.5px]">
          {t(LINE_ITEM_TYPE_KEY[item.type])}
        </span>
      ) : null}
      {wide ? (
        <span className="text-muted-foreground w-[80px] shrink-0 truncate text-[11.5px] tabular-nums">
          {item.startsOn ?? '–'}
        </span>
      ) : null}
      {wide ? (
        <span className="text-muted-foreground w-[80px] shrink-0 truncate text-[11.5px] tabular-nums">
          {item.endsOn ?? '–'}
        </span>
      ) : null}
      <Tooltip
        asChild
        content={
          periodCount > 1
            ? t('contract.estimateTotal', {
                perPeriod: fmt(item.estimateHours),
                count: periodCount,
              })
            : undefined
        }
      >
        <span className="text-muted-foreground w-[74px] shrink-0 text-right text-[11.5px] tabular-nums">
          {hasWork ? fmt(String(estimate)) : '–'}
        </span>
      </Tooltip>
      <span
        className={`w-[74px] shrink-0 text-right text-[12px] font-medium tabular-nums ${
          over ? 'text-destructive' : ''
        }`}
      >
        {hasWork ? fmt(item.trackedHours) : '–'}
      </span>
      {/**
       * ⚠️⚠️ **AN EM-DASH WHERE THERE IS NO FIGURE, NEVER A CONFIDENT ZERO — round 5 item 16.** A
       * time-and-materials deliverable HAS no budget; `0 Ft` would claim one was agreed at nothing.
       * `money()` in `finance-format.ts` keeps the same rule for the same reason.
       *
       * ⚠️⚠️ **AND NONE OF THE THREE IS `wide`-GATED.** *Feladat* already existed and was hidden in
       * the drawer, which is why the report lists three missing columns rather than two: a column
       * that is not drawn is a column that does not exist. Dropping the two figures a contract is
       * argued over, the moment it opens in a drawer, is the drawer saying *go to the page*.
       */}
      {/**
       * ⚠️⚠️ U-5 · **THE WHOLE AGREEMENT, AS THE ESTIMATE BESIDE IT IS.** Budget and income are
       * stored per period on a recurring contract; the cell multiplies by the item's own span and
       * the per-period figure is in the title, the estimate column's own treatment.
       */}
      {/* 22 Oct 2026 · the three money columns, only to a reader of the project's money. */}
      {item.moneyVisible && (
        <>
          <MoneyCell value={budgetFigure(item)} item={item} periods={periods} />
          <MoneyCell value={item.revenue} item={item} periods={periods} />
          {/* 17 Oct 2026 · *Költés* — logged hours at cost, already the whole total; never × periods. */}
          <span className="text-muted-foreground w-[96px] shrink-0 text-right text-[11.5px] tabular-nums">
            {spent === undefined ? '–' : money(Number(spent.cost), currencyOf(item))}
          </span>
        </>
      )}
      <span className="text-muted-foreground w-[68px] shrink-0 text-right text-[11.5px] tabular-nums">
        {item.taskCount}
      </span>

      {/* ⚠️ `sticky right-0` on the card's own background — the menu stays reachable while the
          columns scroll under it, which is the whole reason the row is not a `<table>`. */}
      {/* ⚠️ The cell follows the row's hover with an OPAQUE mix — `bg-accent/40` over nothing
          would let the columns scrolling under it show through. */}
      <span className="bg-background sticky right-0 shrink-0 pl-1 transition-colors group-hover:bg-[color-mix(in_oklch,var(--accent)_40%,var(--background))]">
        <DropdownMenu items={menu} width={230} align="end" label={t('lineItem.actions')}>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground"
            aria-label={t('lineItem.actions')}
          >
            <Icon name="ellipsis" size={14} />
          </Button>
        </DropdownMenu>
      </span>
    </div>
  );
}

/* ── attachments and history ─────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **The same `Attachments` list every comment uses, with an upload of its own.** A comment is a
 * statement made at a moment, so files join it in the composer; a contract is a living record, so a
 * signed PDF can arrive weeks after it was drafted. That difference is the whole reason the upload
 * is the caller's rather than the component's.
 */
function ContractAttachments({
  contract,
  handlers,
  dropped,
}: {
  readonly contract: Contract;
  readonly handlers: ContractDetailHandlers;
  /** ⚠️ Bumped by a drop on the surface — the list has no other way to hear about one. */
  readonly dropped: number;
}) {
  const t = useT();
  const [failure, setFailure] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const upload = async (file: File): Promise<void> => {
    /* ⚠️ No `busy` flag here any more — `Attachments` disables its own control while an upload is
       in flight, which is the one place that knows a scan is running. */
    setFailure(null);
    const code = await handlers.onAttach(file);
    if (code === null) {
      /* ⚠️ The list reads its own data, so it has to be told the set changed. A key bump remounts
       * it, which is one line against a second copy of the fetch living up here. */
      setNonce((count) => count + 1);
      return;
    }
    setFailure(code);
  };

  return (
    <section>
      {failure === null ? null : (
        <p className="text-destructive mb-1 text-[11.5px]">
          {t(ATTACH_ERROR[failure] ?? 'task.attachment.failed')}
        </p>
      )}

      {/* ⚠️ Item 12's shape, and this one hand-rolled a picker, a hidden input and a section
          action to get close to it. The component draws all three now — and the drop target comes
          with them, which the button-only version never had. */}
      <Attachments
        key={`${String(nonce)}-${String(dropped)}`}
        target="contract"
        targetId={contract.id}
        canEdit={handlers.canManage}
        heading={t('contract.attachments')}
        variant="section"
        emptyNote={t('contract.attachments.empty')}
        accept={DOCUMENT_FILES}
        onUpload={async (files) => {
          for (const file of files) await upload(file);
        }}
      />
    </section>
  );
}

/** ⚠️ The four codes C22 made distinct, each with its own sentence — rule 5. */
const ATTACH_ERROR: Readonly<Record<string, MessageKey>> = {
  file_too_large: 'task.attachment.tooLarge',
  file_infected: 'task.attachment.infected',
  scanner_unavailable: 'task.attachment.scannerDown',
  unsupported_file_type: 'task.attachment.failed',
};

function History({ data }: { readonly data: ContractDetailData }) {
  /* ux-pass-2 · the one history (`screens/history/`): searchable, filterable, 25 a page, closed
     until somebody opens it. It replaced a list of sentences over unpaged audit rows. */
  return <CollapsibleHistory type="contract" id={data.contract.id} />;
}

/* ── vocabularies ────────────────────────────────────────────────────────────────────────── */

const CONTRACT_STATUS_OPTIONS = ['draft', 'active', 'closed'] as const;

const TYPE_KEY = {
  recurring: 'contract.type.recurring',
  milestone: 'contract.type.milestone',
  service: 'contract.type.service',
} as const;

const STATUS_KEY = {
  draft: 'contract.status.draft',
  active: 'contract.status.active',
  closed: 'contract.status.closed',
} as const;
